import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { toast } from 'sonner';
import { create } from 'zustand';

import { useAuth } from '@/hooks/use-auth';
import { useBrandKits } from '@/hooks/useBrandKits';
import { useDocumentOutput } from '@/hooks/useDocumentOutput';
import { registerWorkspaceReset } from '@/lib/workspace/workspaceResetRegistry';
import { articleJobService } from '@/services/data/ArticleJobService';
import { updateDocumentMarkdown } from '@/services/data/DocumentsService';
import { logger } from '@/utils/logger';

interface EditorEntry {
  savedMarkdown: string | null;
  conflict: boolean;
  comparisonMarkdown: string | null;
  baseline: string;
  draft: string;
  isEditing: boolean;
  isSaving: boolean;
  error: string | null;
  storageError: boolean;
}
interface EditorState {
  entries: Record<string, EditorEntry>;
  epoch: number;
}
const useEditorState = create<EditorState>(() => ({ entries: {}, epoch: 0 }));
const PREFIX = 'mora:document-draft:v1:';
const dirty = (entry: EditorEntry) => entry.isEditing && entry.draft !== entry.baseline;

/** Drafts survive every Inspector close path, including Escape and the drawer mutex. */
function remember(key: string, entry: EditorEntry): EditorEntry {
  try {
    if (dirty(entry))
      sessionStorage.setItem(
        PREFIX + key,
        JSON.stringify({ baseline: entry.baseline, draft: entry.draft }),
      );
    else sessionStorage.removeItem(PREFIX + key);
    return { ...entry, storageError: false };
  } catch {
    return { ...entry, storageError: dirty(entry) };
  }
}
function put(key: string, entry: EditorEntry): void {
  const next = remember(key, entry);
  useEditorState.setState((state) => ({ entries: { ...state.entries, [key]: next } }));
}
function initial(key: string, markdown: string, isAuthoritative = true): EditorEntry {
  const entry: EditorEntry = {
    savedMarkdown: null,
    conflict: false,
    comparisonMarkdown: null,
    baseline: markdown,
    draft: '',
    isEditing: false,
    isSaving: false,
    error: null,
    storageError: false,
  };
  try {
    const raw = sessionStorage.getItem(PREFIX + key);
    const saved = raw ? JSON.parse(raw) : null;
    if (saved && typeof saved.baseline === 'string' && typeof saved.draft === 'string')
      return {
        ...entry,
        baseline: saved.baseline,
        draft: saved.draft,
        isEditing: true,
        conflict: isAuthoritative && saved.baseline !== markdown,
        error:
          isAuthoritative && saved.baseline !== markdown
            ? 'The saved article changed while this draft was open. Copy your changes before cancelling, then reopen Edit to use the current article.'
            : null,
      };
  } catch {
    /* Malformed local cache is ignored; the persisted article remains authoritative. */
  }
  return entry;
}

// Unlike a component effect this guard survives closing the viewer with a dirty draft.
if (typeof window !== 'undefined')
  window.addEventListener('beforeunload', (event) => {
    let hasDraft = Object.values(useEditorState.getState().entries).some(dirty);
    try {
      for (let index = 0; !hasDraft && index < sessionStorage.length; index++)
        hasDraft = Boolean(sessionStorage.key(index)?.startsWith(PREFIX));
    } catch {
      /* The in-memory fallback above still protects storage-denied drafts. */
    }
    if (hasDraft) {
      event.preventDefault();
      event.returnValue = '';
    }
  });

registerWorkspaceReset('document-markdown-editor', () => {
  // Persisted drafts are private to their actor/workspace/source key. Keep only
  // storage-denied drafts in memory so changing workspace cannot destroy them.
  useEditorState.setState((state) => ({
    epoch: state.epoch + 1,
    entries: Object.fromEntries(
      Object.entries(state.entries)
        .filter(([, entry]) => entry.storageError && dirty(entry))
        .map(([key, entry]) => [key, { ...entry, isSaving: false }]),
    ),
  }));
});

export interface DocumentEditorSource {
  documentId?: string | null;
  messageId?: string | null;
  markdown: string;
  /**
   * The editorial topic this article belongs to. When present, a saved edit is reported to the article
   * service, which rechecks the new text if the workspace proceeds on its own.
   */
  editorialTopicId?: string | null;
}

/** One save/no-op/invalidation path for the chat article and its immersive reader. */
export function useDocumentMarkdownEditor(source: DocumentEditorSource) {
  const { user } = useAuth();
  const { activeKit } = useBrandKits();
  const queryClient = useQueryClient();
  // This is an affordance gate, not authorization. The service and RLS own access.
  const hasSourceIdentity = Boolean(
    user?.id &&
    activeKit?.id &&
    source.messageId &&
    source.documentId &&
    source.messageId !== source.documentId,
  );
  const key = hasSourceIdentity
    ? JSON.stringify([user?.id, activeKit?.id, source.messageId, source.documentId])
    : '';
  const authoritative = useDocumentOutput({
    workspaceId: activeKit?.id,
    actorId: user?.id,
    messageId: source.messageId,
    documentId: source.documentId,
    enabled: hasSourceIdentity,
  });
  const canEdit = hasSourceIdentity && Boolean(authoritative.data) && !authoritative.isError;
  const entry = useEditorState((state) => (key ? state.entries[key] : undefined));
  const epoch = useEditorState((state) => state.epoch);

  useEffect(() => {
    if (key && !useEditorState.getState().entries[key])
      put(key, initial(key, source.markdown, false));
  }, [key, source.markdown, epoch]);

  useEffect(() => {
    if (!key || !authoritative.data) return;
    const latest = authoritative.data.markdown;
    const current = useEditorState.getState().entries[key] ?? initial(key, latest);
    const conflict = dirty(current) && latest !== current.baseline;
    put(key, {
      ...current,
      savedMarkdown: latest,
      ...(conflict
        ? {
            conflict: true,
            error:
              'This article changed while your draft was open. Reload the saved version to compare; your draft will be kept.',
          }
        : {}),
    });
  }, [key, authoritative.data, authoritative.dataUpdatedAt, epoch]);

  const current = () =>
    useEditorState.getState().entries[key] ??
    initial(key, authoritative.data?.markdown ?? source.markdown, Boolean(authoritative.data));
  const markdown =
    entry?.savedMarkdown ??
    authoritative.data?.markdown ??
    (!hasSourceIdentity || authoritative.isPending ? source.markdown : '');
  const reloadSavedVersion = async () => {
    if (!key || current().isSaving) return;
    const reloadEpoch = useEditorState.getState().epoch;
    const latest = await authoritative.refetch();
    if (useEditorState.getState().epoch !== reloadEpoch) return;
    const value = current();
    if (latest.error || !latest.data) {
      put(key, {
        ...value,
        error: 'The saved article could not be loaded. Your draft is still here. Try again.',
      });
      return;
    }
    // This explicit action advances the comparison baseline, not the draft. A
    // subsequent Save remains a separate decision and still uses server CAS.
    put(key, {
      ...value,
      savedMarkdown: latest.data.markdown,
      baseline: latest.data.markdown,
      comparisonMarkdown: latest.data.markdown,
      conflict: false,
      error: null,
    });
  };
  const beginEdit = () => {
    if (!canEdit) return;
    const value = current();
    if (value.isSaving) return;
    const baseline = value.savedMarkdown ?? authoritative.data?.markdown ?? source.markdown;
    put(key, {
      ...value,
      baseline,
      draft: baseline,
      isEditing: true,
      conflict: false,
      comparisonMarkdown: null,
      error: null,
    });
  };
  const cancelEdit = () => {
    if (!key) return;
    const value = current();
    if (value.isSaving) return;
    put(key, {
      ...value,
      isEditing: false,
      draft: '',
      conflict: false,
      comparisonMarkdown: null,
      error: null,
    });
  };
  const setDraft = (draft: string) => {
    if (!key) return;
    const value = current();
    if (!value.isSaving) put(key, { ...value, draft });
  };
  // The save is a browser call to the database, which cannot start a workflow, so the server is told about it
  // here. Best effort by design: the save has already succeeded and a failure only leaves the checks stale,
  // as they were before.
  const reportSavedEdit = async (): Promise<boolean> => {
    if (!source.editorialTopicId || !activeKit?.id || !source.documentId) return false;
    try {
      const result = await articleJobService.afterSavedEdit({
        workspaceId: activeKit.id,
        topicId: source.editorialTopicId,
        documentId: source.documentId,
      });
      if (result.state !== 'queued' && result.state !== 'existing') return false;
      for (const queryKey of [
        ['article-review-job', activeKit.id, source.editorialTopicId],
        ['article-workspace-jobs', activeKit.id],
      ])
        void queryClient.invalidateQueries({ queryKey });
      return true;
    } catch (error) {
      logger.warn('[document-editor] The saved edit could not be reported for a recheck', {
        message: error instanceof Error ? error.message : String(error),
      });
      return false;
    }
  };
  const save = async () => {
    if (!canEdit || !source.messageId || !source.documentId || !user?.id) return;
    const value = current();
    if (value.isSaving || !value.isEditing) return;
    if (value.draft === value.baseline) {
      cancelEdit();
      return;
    }
    if (!value.draft.trim()) {
      put(key, { ...value, error: 'The article cannot be empty.' });
      return;
    }
    if (value.conflict) return;
    const saveEpoch = useEditorState.getState().epoch;
    put(key, { ...value, isSaving: true, error: null });
    try {
      const result = await updateDocumentMarkdown(
        source.messageId,
        source.documentId,
        value.draft,
        user.id,
        value.baseline,
      );
      if (useEditorState.getState().epoch !== saveEpoch) return;
      if (!result.ok) {
        const error = ('error' in result && result.error) || 'Could not save document';
        put(key, {
          ...value,
          isSaving: false,
          conflict: 'code' in result && result.code === 'conflict',
          error,
        });
        toast.error(error);
        return;
      }
      put(key, {
        ...value,
        savedMarkdown: value.draft,
        baseline: value.draft,
        draft: '',
        isEditing: false,
        isSaving: false,
        conflict: false,
        comparisonMarkdown: null,
        error: null,
      });
      for (const queryKey of [
        ['document-output', activeKit?.id, user.id, source.messageId, source.documentId],
        ['editorial-article-trail', source.messageId],
        ['editorial-article-scores'],
        ['documents'],
        ['drawer-document', source.messageId],
        ['plan-documents-markdown', source.messageId],
        ['plan-documents'],
        ['campaign-documents'],
        ['desk'],
        ['desk-workspace'],
      ])
        void queryClient.invalidateQueries({ queryKey });
      void authoritative.refetch();
      const recheckQueued = await reportSavedEdit();
      toast.success(recheckQueued ? 'Saved. Checking the new version…' : 'Document saved');
    } catch (error) {
      if (useEditorState.getState().epoch !== saveEpoch) return;
      const message = error instanceof Error ? error.message : 'Could not save document';
      put(key, { ...value, isSaving: false, error: message });
      toast.error(message);
    }
  };
  return {
    canEdit,
    hasSourceIdentity,
    isLoadingArticle: hasSourceIdentity && authoritative.isPending,
    loadError:
      hasSourceIdentity &&
      !authoritative.isPending &&
      (authoritative.isError || !authoritative.data)
        ? 'The saved article could not be loaded. Try opening it again.'
        : null,
    hasAuthoritativeContent: Boolean(authoritative.data),
    conflict: Boolean(entry?.conflict),
    comparisonMarkdown: entry?.comparisonMarkdown ?? null,
    isReloading: authoritative.isFetching,
    reloadSavedVersion,
    markdown,
    isEditing: Boolean(entry?.isEditing),
    isSaving: Boolean(entry?.isSaving),
    draft: entry?.draft ?? '',
    isDirty: entry ? dirty(entry) : false,
    error: entry?.error ?? null,
    storageError: entry?.storageError ?? false,
    beginEdit,
    cancelEdit,
    setDraft,
    save,
  };
}

/** Test seam: does not affect the persisted document or other workspace stores. */
export function resetDocumentEditorsForTest(): void {
  useEditorState.setState({ entries: {}, epoch: useEditorState.getState().epoch + 1 });
}
